'use client'

import { AnimatePresence, LayoutGroup, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import { RadarIcon, SparkleIcon, TrendIcon } from '@/components/shell-icons'
import { Button } from '@/components/ui/button'
import { RollingNumber } from '@/components/ui/rolling-number'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { Skeleton } from '@/components/ui/skeleton'
import { CryptoIcon } from '@/components/widget/crypto-icon'
import { useDashboardCommands } from '@/features/dashboard/hooks/use-dashboard-commands'
import { HOME_EASE, HOME_SPRING, HomePanel } from '@/features/home/components/home-motion'
import { type HomeMarket, useHomeMarkets } from '@/features/home/hooks/use-home-markets'
import { useNow } from '@/hooks/use-now'
import { useNumberFormat } from '@/hooks/use-number-format'
import { getExchangeLabel } from '@/lib/configs/exchange-registry'
import { formatFundingCountdown, nextFundingDate } from '@/lib/next-funding'
import { cn } from '@/lib/utils'
import { displayPriceOf } from '@/lib/utils/display-price'
import { formatCompact } from '@/lib/utils/format-quantity'

type MarketView = 'top' | 'gainers' | 'losers'

const LIST_ROWS = 8

function changeOf(market: HomeMarket): number | null {
  const n = Number(market.ticker?.priceChangePercent24h)
  return Number.isFinite(n) && market.ticker?.priceChangePercent24h ? n : null
}

function ChangePill({ change, className }: { change: number | null; className?: string }) {
  const fmt = useNumberFormat()
  if (change === null) return <span className={cn('text-foreground/30 text-xs', className)}>—</span>
  const up = change >= 0
  return (
    <span
      className={cn(
        'rounded-xs px-1.5 py-0.5 text-xs font-medium whitespace-nowrap transition-colors duration-300',
        up ? 'bg-chart-mint/10 text-chart-mint' : 'bg-chart-red/10 text-chart-red',
        className
      )}
    >
      {up ? '+' : '−'}
      {fmt.number(Math.abs(change), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
    </span>
  )
}

/** Where the last price sits in today's low–high range, with the marker gliding as it moves. */
function RangeBar({ market }: { market: HomeMarket }) {
  const t = useTranslations('trade.home.markets')
  const fmt = useNumberFormat()
  const low = Number(market.ticker?.low24h)
  const high = Number(market.ticker?.high24h)
  const price = Number(displayPriceOf(market.ticker))
  if (!(low > 0) || !(high > low) || !(price > 0)) return null
  const share = Math.min(1, Math.max(0, (price - low) / (high - low)))
  const digits = {
    minimumFractionDigits: market.priceDecimals,
    maximumFractionDigits: market.priceDecimals,
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-foreground/45 flex justify-between text-[11px]">
        <span>{t('low', { price: fmt.number(low, digits) })}</span>
        <span>{t('range24h')}</span>
        <span>{t('high', { price: fmt.number(high, digits) })}</span>
      </div>
      <div className="from-chart-red/50 via-foreground/20 to-chart-mint/50 relative h-1 rounded-full bg-linear-to-r">
        <motion.span
          className="bg-foreground absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-(--shell-card-shadow)"
          initial={false}
          animate={{ left: `${share * 100}%` }}
          transition={HOME_SPRING}
        />
      </div>
    </div>
  )
}

function FocusStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-foreground/45 text-[11px]">{label}</span>
      <span className="text-foreground text-sm">{value}</span>
    </div>
  )
}

/**
 * The market at a glance, like TradingView's home: the venue's main perps with live prices and
 * 24h moves (Top / Gainers / Losers re-sort with the rows gliding into place), and the picked
 * asset in focus — today's range, volume, open interest, funding with its countdown — with the
 * next step one click away: draft a trade, ask the AI why it moved, or open the Screener.
 */
export function HomeMarkets({
  onOpenScreener,
  className,
}: {
  onOpenScreener: () => void
  className?: string
}) {
  const t = useTranslations('trade.home.markets')
  const fmt = useNumberFormat()
  const now = useNow(1000)
  const { startTradePrompt, startResearchPrompt } = useDashboardCommands()
  const { exchange, markets, isPending } = useHomeMarkets()
  const [view, setView] = useState<MarketView>('top')
  const [selectedBase, setSelectedBase] = useState<string | null>(null)

  const rows = useMemo(() => {
    if (view === 'top') return markets.slice(0, LIST_ROWS)
    // Gainers are only what is up on the day and losers only what is down — a red row under
    // "Gainers" would read as a bug.
    const movers = markets.filter((market) => {
      const change = changeOf(market)
      return change !== null && (view === 'gainers' ? change > 0 : change < 0)
    })
    const sorted = [...movers].sort((a, b) =>
      view === 'gainers'
        ? (changeOf(b) ?? 0) - (changeOf(a) ?? 0)
        : (changeOf(a) ?? 0) - (changeOf(b) ?? 0)
    )
    return sorted.slice(0, LIST_ROWS)
  }, [markets, view])

  const selected =
    markets.find((market) => market.base === selectedBase) ?? rows[0] ?? markets[0] ?? null
  const selectedPrice = selected ? displayPriceOf(selected.ticker) : undefined
  const funding = selected?.ticker?.fundingRate ? Number(selected.ticker.fundingRate) : null
  const fundingAt = nextFundingDate(selected?.ticker?.nextFundingTime)

  return (
    <HomePanel
      title={t('title')}
      className={className}
      aside={
        <>
          <span className="text-foreground/40 hidden text-xs whitespace-nowrap md:inline">
            {getExchangeLabel(exchange)}
          </span>
          <SegmentedControl
            layoutId="home-markets-view"
            size="xs"
            value={view}
            onChange={setView}
            options={[
              { value: 'top', label: t('views.top') },
              { value: 'gainers', label: t('views.gainers') },
              { value: 'losers', label: t('views.losers') },
            ]}
          />
        </>
      }
    >
      {isPending && markets.length === 0 ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-9 w-full" />
            ))}
          </div>
          <Skeleton className="h-full min-h-48 w-full" />
        </div>
      ) : markets.length === 0 ? (
        <p className="text-foreground/50 py-6 text-sm">{t('empty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <LayoutGroup id="home-markets">
            <ul className="flex flex-col">
              {rows.length === 0 && (
                <li className="text-foreground/45 px-2 py-6 text-sm">
                  {t(view === 'gainers' ? 'noGainers' : 'noLosers')}
                </li>
              )}
              <AnimatePresence initial={false} mode="popLayout">
                {rows.map((market) => {
                  const price = displayPriceOf(market.ticker)
                  const isSelected = market.base === selected?.base
                  return (
                    <motion.li
                      key={market.instrument.symbol}
                      layout
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 8, transition: { duration: 0.12 } }}
                      transition={HOME_SPRING}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedBase(market.base)}
                        aria-pressed={isSelected}
                        className="group relative flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-left"
                      >
                        {isSelected && (
                          <motion.span
                            layoutId="home-market-selected"
                            transition={HOME_SPRING}
                            className="bg-foreground/[0.07] absolute inset-0 rounded-md"
                          />
                        )}
                        <span className="group-hover:bg-foreground/[0.04] absolute inset-0 rounded-md transition-colors" />
                        <CryptoIcon symbol={market.base} className="relative size-6 shrink-0" />
                        <span className="relative flex min-w-0 flex-1 flex-col">
                          <span className="text-foreground text-sm font-medium tracking-tight">
                            {market.base}
                          </span>
                          <span className="text-foreground/40 truncate text-[11px]">
                            {market.instrument.symbol}
                          </span>
                        </span>
                        <span className="relative text-sm">
                          {price ? (
                            <RollingNumber
                              text={fmt.number(price, {
                                minimumFractionDigits: market.priceDecimals,
                                maximumFractionDigits: market.priceDecimals,
                              })}
                            />
                          ) : (
                            <span className="text-foreground/30">—</span>
                          )}
                        </span>
                        <ChangePill
                          change={changeOf(market)}
                          className="relative w-16 text-right"
                        />
                      </button>
                    </motion.li>
                  )
                })}
              </AnimatePresence>
            </ul>
          </LayoutGroup>

          {selected && (
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={selected.base}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.24, ease: HOME_EASE }}
                className="bg-foreground/[0.03] flex flex-col gap-4 rounded-md p-4"
              >
                <div className="flex items-start gap-3">
                  <CryptoIcon symbol={selected.base} className="size-9 shrink-0" />
                  <div className="flex min-w-0 flex-col">
                    <span className="text-foreground/50 text-xs">{selected.instrument.symbol}</span>
                    {selectedPrice ? (
                      <RollingNumber
                        text={fmt.number(selectedPrice, {
                          minimumFractionDigits: selected.priceDecimals,
                          maximumFractionDigits: selected.priceDecimals,
                        })}
                        className="font-display text-foreground text-3xl leading-tight tracking-tight"
                      />
                    ) : (
                      <Skeleton className="mt-1 h-8 w-40" />
                    )}
                  </div>
                  <ChangePill change={changeOf(selected)} className="ml-auto" />
                </div>
                <RangeBar market={selected} />
                <div className="grid grid-cols-3 gap-3">
                  <FocusStat
                    label={t('volume')}
                    value={
                      Number(selected.ticker?.volume24h) > 0
                        ? formatCompact(Number(selected.ticker?.volume24h), 2)
                        : '—'
                    }
                  />
                  <FocusStat
                    label={t('openInterest')}
                    value={
                      Number(selected.ticker?.openInterest) > 0
                        ? formatCompact(Number(selected.ticker?.openInterest), 2)
                        : '—'
                    }
                  />
                  <FocusStat
                    label={t('funding')}
                    value={
                      funding === null || !Number.isFinite(funding)
                        ? '—'
                        : `${fmt.number(funding * 100, { maximumFractionDigits: 4 })}%${
                            fundingAt
                              ? ` · ${formatFundingCountdown(fundingAt.getTime() - now)}`
                              : ''
                          }`
                    }
                  />
                </div>
                <div className="mt-auto flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => startTradePrompt(`buy ${selected.base} `)}
                  >
                    <TrendIcon />
                    {t('trade', { asset: selected.base })}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    className="flex-1"
                    onClick={() =>
                      startResearchPrompt(
                        `What's driving ${selected.base}'s price today? Explain it simply.`
                      )
                    }
                  >
                    <SparkleIcon />
                    {t('askWhy')}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onOpenScreener}>
                    <RadarIcon />
                    {t('screener')}
                  </Button>
                </div>
                <p className="text-foreground/40 text-[11px]">{t('tradeHint')}</p>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      )}
    </HomePanel>
  )
}
